import type { ModuleSlug } from "./brochureModules";

/**
 * Phase 5 — soft-lock route table.
 *
 * Free-tier users (non-Operator) still navigate to module pages so the
 * sidebar/navigation stays honest about what the product offers — but the
 * LockedShell wrapper renders the page in a non-interactive preview mode
 * with a brochure CTA. The actual click-through goes to /brochure/<slug>.
 *
 * Match semantics:
 *   - `pathname === prefix` → locked (covers exact module landing pages).
 *   - `pathname.startsWith(prefix + "/")` → locked (covers all sub-routes).
 *   - A trailing-slash prefix is NOT used; we add the "/" boundary on the fly
 *     so /attendance doesn't accidentally swallow /attendance-dashboard etc.
 *
 * Order matters when prefixes overlap: longer / more-specific prefixes win.
 * The lookup walks the list in order and stops at the first match — keep
 * any "deep" prefix (e.g. `/settings/payroll/...`) ABOVE its broader sibling
 * (`/payroll`) so the deeper one is reached first. None of the entries in
 * the current table actually conflict, but the helper is implemented so this
 * future-proofs the table.
 */

export interface LockedRoutePattern {
  /** Path prefix; route is locked if location.pathname matches it exactly or has it as a "/"-bounded prefix. */
  prefix: string;
  /** Module slug for the brochure CTA + chip color. */
  module: ModuleSlug;
}

export const LOCKED_ROUTES: LockedRoutePattern[] = [
  // ---- Attendance ----
  // Settings is listed before the generic /attendance so a /settings/attendance
  // path resolves under the more specific entry without ambiguity.
  { prefix: "/settings/attendance", module: "attendance" },
  { prefix: "/attendance-dashboard", module: "attendance" },
  { prefix: "/duty-roster-management", module: "attendance" },
  { prefix: "/attendance", module: "attendance" },

  // ---- Leaves ----
  { prefix: "/my-leaves", module: "leaves" },
  { prefix: "/leave-approvals", module: "leaves" },
  { prefix: "/team-calendar", module: "leaves" },
  { prefix: "/leave-statements", module: "leaves" },
  { prefix: "/leave-balances", module: "leaves" },
  { prefix: "/absence-analytics", module: "leaves" },
  { prefix: "/leave-policies", module: "leaves" },
  { prefix: "/statutory-leave-templates", module: "leaves" },
  { prefix: "/delegations", module: "leaves" },
  { prefix: "/restricted-holidays", module: "leaves" },

  // ---- Recruitment ----
  { prefix: "/settings/recruitment", module: "recruitment" },
  { prefix: "/recruitment-dashboard", module: "recruitment" },
  { prefix: "/recruitment", module: "recruitment" },
  // Aliases that the rocketnew-style pages deep-link to before redirecting.
  { prefix: "/job-openings-management", module: "recruitment" },
  { prefix: "/candidates-management-center", module: "recruitment" },
  { prefix: "/hiring-pipeline", module: "recruitment" },
  { prefix: "/interviews-management", module: "recruitment" },
  { prefix: "/offers-management", module: "recruitment" },

  // ---- Employee Impact (engagement + performance) ----
  { prefix: "/settings/employee-impact", module: "impact" },
  { prefix: "/employee-impact", module: "impact" },
  { prefix: "/hr-enps-analytics", module: "impact" },
  { prefix: "/check-ins-hub", module: "impact" },
  { prefix: "/performance-snapshot", module: "impact" },
  { prefix: "/behavior-and-values", module: "impact" },
  { prefix: "/ai-impact-analysis", module: "impact" },

  // ---- Development ----
  { prefix: "/employee-development-hub", module: "development" },
  { prefix: "/development-objectives-creator", module: "development" },
  { prefix: "/development-objective-detail", module: "development" },
  { prefix: "/team-development-dashboard", module: "development" },

  // ---- Payroll ----
  // Settings before the generic /payroll so /settings/payroll resolves there.
  { prefix: "/settings/payroll", module: "payroll" },
  { prefix: "/payroll-run-wizard", module: "payroll" },
  { prefix: "/payroll-documents-center", module: "payroll" },
  { prefix: "/payroll-reports", module: "payroll" },
  { prefix: "/eos-center", module: "payroll" },
  { prefix: "/payroll", module: "payroll" },

  // ---- Tournament ----
  { prefix: "/tournament-management", module: "tournament" },

  // ---- Projects ----
  { prefix: "/projects-list", module: "projects" },
  { prefix: "/task-board", module: "projects" },
  { prefix: "/settings/project-management", module: "projects" },
];

/**
 * Returns the locked-module slug for a pathname, or null if the route is open.
 *
 * Match rules (kept in sync with backend TierEnforcementMiddleware):
 *   - exact equality, or
 *   - prefix followed by `/` (so `/attendance` never swallows `/attendance-dashboard`).
 *
 * The list is scanned in order; the first matching entry wins. Order entries
 * so that any longer/more-specific prefix appears BEFORE its shorter sibling.
 */
export function getLockedModule(pathname: string): ModuleSlug | null {
  if (!pathname) return null;
  const match = LOCKED_ROUTES.find(
    (r) => pathname === r.prefix || pathname.startsWith(r.prefix + "/"),
  );
  return match?.module ?? null;
}
